// components/ExportOrdersModal.js
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  FormControl,
  FormLabel,
  RadioGroup,
  Radio,
  FormControlLabel,
  Checkbox,
  Alert,
  CircularProgress,
  Box,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import mixpanel from 'mixpanel-browser';
import { dashboardAPI } from '../services/dashboard-api';

const toIsoDate = (d) => {
  if (!d) return null;
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const ExportOrdersModal = ({ open, onClose, initialFilters }) => {
  const [startDate, setStartDate] = useState(initialFilters?.start_date ?? new Date(Date.now() - 30 * 24 * 60 * 60 * 1000));
  const [endDate, setEndDate] = useState(initialFilters?.end_date ?? new Date());
  const [direction, setDirection] = useState('received');
  const [approvedOnly, setApprovedOnly] = useState(initialFilters?.status === 'approved');
  const [includeAllItems, setIncludeAllItems] = useState(false);
  const [includeDeclined, setIncludeDeclined] = useState(false);
  const [partnerRetailer] = useState(initialFilters?.partner_retailer ?? '');
  const [format, setFormat] = useState('xlsx');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Re-sync from initialFilters when reopened
  useEffect(() => {
    if (open) {
      if (initialFilters?.start_date) setStartDate(initialFilters.start_date);
      if (initialFilters?.end_date) setEndDate(initialFilters.end_date);
      setApprovedOnly(initialFilters?.status === 'approved');
      setIncludeAllItems(false);
      setIncludeDeclined(false);
      setError(null);
    }
  }, [open, initialFilters]);

  const datesValid = startDate && endDate && endDate >= startDate;

  const handleDownload = async () => {
    if (!datesValid) {
      setError('Please choose a valid date range (end date on or after start date).');
      return;
    }
    setSubmitting(true);
    setError(null);

    const params = {
      start_date: toIsoDate(startDate),
      end_date: toIsoDate(endDate),
      direction,
      approved_only: approvedOnly,
      include_declined: includeDeclined && !approvedOnly,
      include_all_items: includeAllItems,
      format,
    };
    if (partnerRetailer) params.partner_retailer = partnerRetailer;

    try {
      const { rowCount } = await dashboardAPI.downloadCommissionsExport(params);
      const dateRangeDays = Math.round((endDate - startDate) / (1000 * 60 * 60 * 24)) + 1;
      try {
        if (mixpanel && mixpanel.__loaded) {
          mixpanel.track('Retailer Export Downloaded', {
            format,
            direction,
            approved_only: approvedOnly,
            include_declined: params.include_declined,
            include_all_items: includeAllItems,
            row_count: rowCount,
            date_range_days: dateRangeDays,
          });
        }
      } catch (_e) { /* tracking is best-effort */ }
      onClose();
    } catch (e) {
      if (e.status === 413 && e.rowCount) {
        setError(
          `Your selection would export ${e.rowCount.toLocaleString()} rows ` +
          `(limit ${e.maxRows.toLocaleString()}). ${e.hint || 'Please narrow the date range.'}`
        );
      } else {
        setError(e.message || 'Export failed. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Export orders</DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <DatePicker
              label="Start date"
              value={startDate}
              onChange={setStartDate}
              slotProps={{ textField: { fullWidth: true, size: 'small' } }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <DatePicker
              label="End date"
              value={endDate}
              onChange={setEndDate}
              slotProps={{ textField: { fullWidth: true, size: 'small' } }}
            />
          </Grid>

          <Grid size={{ xs: 12 }}>
            <FormControl>
              <FormLabel>Direction</FormLabel>
              <RadioGroup row value={direction} onChange={(e) => setDirection(e.target.value)}>
                <FormControlLabel value="received" control={<Radio />} label="Received (orders we got)" />
                <FormControlLabel value="sent" control={<Radio />} label="Sent (orders we referred)" />
                <FormControlLabel value="all" control={<Radio />} label="Both" />
              </RadioGroup>
            </FormControl>
          </Grid>

          <Grid size={{ xs: 12 }}>
            <FormControlLabel
              control={<Checkbox checked={approvedOnly} onChange={(e) => setApprovedOnly(e.target.checked)} />}
              label="Only show approved commissions"
            />
            <Box>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={includeDeclined && !approvedOnly}
                    disabled={approvedOnly}
                    onChange={(e) => setIncludeDeclined(e.target.checked)}
                  />
                }
                label="Include declined items"
              />
            </Box>
            <Box>
              <FormControlLabel
                control={<Checkbox checked={includeAllItems} onChange={(e) => setIncludeAllItems(e.target.checked)} />}
                label="Include all line items from each order (received only)"
              />
            </Box>
          </Grid>

          <Grid size={{ xs: 12 }}>
            <FormControl>
              <FormLabel>Format</FormLabel>
              <RadioGroup row value={format} onChange={(e) => setFormat(e.target.value)}>
                <FormControlLabel value="xlsx" control={<Radio />} label="Excel (.xlsx)" />
                <FormControlLabel value="csv" control={<Radio />} label="CSV (.csv)" />
              </RadioGroup>
            </FormControl>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button
          onClick={handleDownload}
          variant="contained"
          disabled={submitting || !datesValid}
          startIcon={submitting ? <CircularProgress size={16} /> : null}
        >
          {submitting ? 'Generating…' : 'Download'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ExportOrdersModal;
